/**
 * What the research card says about itself, from the snapshot only (plan K0/K1: TP's look, our real data).
 * Nothing here is timed or scripted: a state is what the BFF reports, a clock is the session's own.
 */
import type { SessionSnapshot, StepState } from '@/contracts/onboarding-session';

import { isTerminalState } from './jobState';

/** The jobs the BFF starts at the website step: the card is "done" when every one of them it lists is terminal. */
export const RESEARCH_JOB_KINDS = ['siteProfile', 'research', 'adDiscovery:meta', 'funnel'] as const;

export type ResearchPhase =
  | { kind: 'running' }
  | { kind: 'done' }
  /** Every job ended, but some did not finish cleanly: `error` when any is unavailable, else `warning` (partial). */
  | { kind: 'gaps'; tone: 'warning' | 'error' };

/**
 * The states of the research jobs the snapshot lists. A job it does not list has not started: it is left out, so it
 * is neither a gap (never "unavailable") nor a finish (never "done"), and it does not hold the card back either.
 */
function listedJobStates(snapshot: SessionSnapshot): StepState[] {
  return RESEARCH_JOB_KINDS.flatMap((kind) => {
    const state = snapshot.jobs.find((j) => j.kind === kind)?.state;
    return state === undefined ? [] : [state];
  });
}

export function researchPhase(snapshot: SessionSnapshot | null): ResearchPhase {
  if (!snapshot) return { kind: 'running' };
  const states = listedJobStates(snapshot);
  // Nothing listed yet: the research has not begun, which is not the same as it having finished.
  if (states.length === 0 || states.some((s) => s === 'queued' || s === 'running')) return { kind: 'running' };
  if (states.includes('unavailable')) return { kind: 'gaps', tone: 'error' };
  if (states.includes('partial')) return { kind: 'gaps', tone: 'warning' };
  return { kind: 'done' };
}

export interface ResearchClock {
  /** When the BFF session was created; null without a snapshot (nothing to count from). */
  startedAt: number | null;
  /** When the last research job reached its state, once every one is terminal; null while any is still working. */
  frozenAt: number | null;
}

/**
 * Session age for the counter: `snapshot.createdAt` to now while the research works, then frozen at the last job's
 * `updatedAt` (so a resumed, finished session reads the time it took, not the time since). The frozen time is never
 * before the start, and `formatElapsed` never writes a negative (a `createdAt` ahead of this browser's clock reads 0:00).
 */
export function researchClock(snapshot: SessionSnapshot | null): ResearchClock {
  const startedAt = snapshot ? Date.parse(snapshot.createdAt) : Number.NaN;
  if (!snapshot || Number.isNaN(startedAt)) return { startedAt: null, frozenAt: null };
  if (researchPhase(snapshot).kind === 'running') return { startedAt, frozenAt: null };
  const ended = snapshot.jobs
    .filter((j) => (RESEARCH_JOB_KINDS as readonly string[]).includes(j.kind) && isTerminalState(j.state))
    .map((j) => Date.parse(j.updatedAt))
    .filter((t) => !Number.isNaN(t));
  return { startedAt, frozenAt: Math.max(startedAt, ...ended) };
}

/** `m:ss` (`floor(ms / 1000)`), as TP's counter writes it. */
export function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
